import React from 'react'
import PropTypes from 'prop-types'
import { useAnalyticsPageView } from '@chilecompra/react-kit'
import { Skeleton, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'

import { ABILITY_STATUS_TYPES, USER_TYPE } from '../../config/settings/constant'
import PrivateSection from '../../components/Security/PrivateSection'
import PublicSection from '../../components/Security/PublicSection'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import SiiInformationSection from '../SiiInformationSection/SiiInformationSection.container'
import { Link, TableCell, TableContainer, TextSpacer, Bold } from './BasicSellerInformationSection.styles'

/**
 * The table seller information container.
 */
const TableSellerInfo = props => {
  const {
    EMTClassificationDetail,
    abilityStatus,
    accreditationEndDate,
    contractStatus,
    error,
    isForeign,
    isMinorSizeSeller,
    loading,
    options,
    orgAddress,
    orgName,
    toggleEMTModal,
    unlimitedMembership
  } = props

  const { isPrivated, userType } = useAuthorization()
  const gotoAbility = options[1].route

  useAnalyticsPageView('/ficha/')

  return (
    <>
      {loading && (
        <PublicSection>
          <TablePlaceholder />
        </PublicSection>
      )}

      {!error && !loading && userType !== USER_TYPE.PUBLIC && (
        <PublicSection>
          <Typography fontWeight="bold" variant="h4">
            Información del proveedor
          </Typography>
        </PublicSection>
      )}

      {!error && !loading && (
        <TableContainer>
          <Table accessibility={{ label: 'Información del proveedor' }}>
            <TableRow>
              <TableCell enableHead padding="12px 0" width="160px">
                Nombre de fantasía
              </TableCell>

              <TableCell padding="12px 0">{orgName}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell enableHead padding="12px 0" width="160px">
                Estado de habilidad
              </TableCell>

              <PrivateSection>
                <TableCell padding="12px 0">
                  <Link secondary textDecoration to={gotoAbility}>
                    {abilityStatus === 'INHABIL' && ABILITY_STATUS_TYPES[abilityStatus]?.description}
                  </Link>

                  {abilityStatus !== 'INHABIL' &&
                    (ABILITY_STATUS_TYPES[abilityStatus]?.description || 'Sin información disponible')}
                </TableCell>
              </PrivateSection>

              <PublicSection>
                <TableCell padding="12px 0">
                  {ABILITY_STATUS_TYPES[abilityStatus]?.description || 'Sin información disponible'}
                </TableCell>
              </PublicSection>
            </TableRow>

            <TableRow>
              <TableCell enableHead padding="12px 0" disableBottomBorder={!isForeign && !isPrivated} width="160px">
                Estado de acreditación
              </TableCell>

              <TableCell disableBottomBorder={!isForeign && !isPrivated} padding="12px 0">
                {contractStatus === 0
                  ? 'Proveedor sin acreditación vigente en Mercado Público'
                  : `Proveedor acreditado ${!unlimitedMembership ? `hasta ${accreditationEndDate}` : 'indefinido.'}
              `}
              </TableCell>
            </TableRow>

            <PrivateSection>
              <TableRow>
                <TableCell disableBottomBorder={isForeign && isPrivated} enableHead padding="12px 0" width="160px">
                  Domicilio legal
                </TableCell>

                <TableCell padding="12px 0" disableBottomBorder={isForeign && isPrivated}>
                  {orgAddress?.loading && <Skeleton />}

                  {!orgAddress?.error && !orgAddress?.loading && orgAddress?.value}

                  {orgAddress?.error && !orgAddress?.loading && (
                    <Toast disableBackground disableIcon severity="default" title="Información no disponible.">
                      Estamos presentando problemas para disponibilizar esta información, por favor intenta en unos
                      minutos más.
                    </Toast>
                  )}
                </TableCell>
              </TableRow>
              <SiiInformationSection />

              {!isForeign && (
                <>
                  <TableRow>
                    <TableCell enableHead padding="12px 0" disableBottomBorder width="160px">
                      Empresa de Menor Tamaño (EMT)
                    </TableCell>

                    <TableCell disableBottomBorder padding="12px 0">
                      {!isMinorSizeSeller &&
                      EMTClassificationDetail?.isForced &&
                      EMTClassificationDetail?.forcedReason === 'CONTROLADO POR SOCIEDAD NO EMT' ? (
                        <>
                          Aunque en el Servicio de Impuestos Internos tu empresa figura como EMT, en Mercado Público{' '}
                          <Bold>no clasifica</Bold> como tal porque está controlada por una sociedad que no es EMT.
                        </>
                      ) : (
                        <>
                          <Bold>{isMinorSizeSeller ? 'Si' : 'No'}</Bold> clasifica como Empresa de Menor Tamaño según
                          información del Servicio de Impuestos Internos.
                        </>
                      )}

                      <TextSpacer />
                      <Link textDecoration onClick={toggleEMTModal}>
                        Revisa más información sobre Empresa de Menor Tamaño (EMT)
                      </Link>
                    </TableCell>
                  </TableRow>
                </>
              )}
            </PrivateSection>
          </Table>
        </TableContainer>
      )}
    </>
  )
}

TableSellerInfo.defaultProps = {
  abilityStatus: '',
  accreditationEndDate: '',
  contractStatus: null,
  error: undefined,
  isMinorSizeSeller: true,
  loading: false,
  orgAddress: {
    error: false,
    loading: false,
    value: ''
  },
  orgName: ''
}

TableSellerInfo.propTypes = {
  EMTClassificationDetail: PropTypes.shape({
    isForced: PropTypes.bool,
    forcedReason: PropTypes.string
  }),
  abilityStatus: PropTypes.string,
  accreditationEndDate: PropTypes.string,
  contractStatus: PropTypes.number,
  error: PropTypes.shape({
    code: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    rease: PropTypes.string.isRequired
  }),
  isForeign: PropTypes.bool.isRequired,
  isMinorSizeSeller: PropTypes.bool,
  loading: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node.isRequired,
      current: PropTypes.bool.isRequired,
      label: PropTypes.string.isRequired,
      route: PropTypes.string.isRequired
    })
  ).isRequired,
  orgAddress: PropTypes.shape({
    error: PropTypes.bool,
    loading: PropTypes.bool,
    value: PropTypes.string
  }),
  orgName: PropTypes.string,
  toggleEMTModal: PropTypes.func.isRequired,
  unlimitedMembership: PropTypes.bool
}

export default TableSellerInfo
